import { X, FileText, Image as ImageIcon, Link2 } from 'lucide-react';
import { MediaUploadArea } from './MediaUploadArea';
import { MediaListItem } from './MediaListItem';
import { LinksList } from './LinksList';

interface MediaData {
  id: number;
  name: string;
  size: number;
  created_at: string;
}

interface MediaAttachmentModalProps {
  isOpen: boolean;
  activeTab: 'documents' | 'images' | 'links';
  documentsData: MediaData[];
  imagesData: MediaData[];
  currentUrls: string[];
  linkInput: string;
  linkError: string;
  isUploading: boolean;
  isDeleting: boolean;
  maxItemsPerCategory: number;
  allowedDocumentExtensions: string[];
  allowedImageExtensions: string[];
  readOnly?: boolean;
  onClose: () => void;
  onTabChange: (tab: 'documents' | 'images' | 'links') => void;
  onDocumentUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDownload: (id: number, name: string, type: string) => void;
  onDelete: (id: number) => void;
  onLinkInputChange: (value: string) => void;
  onAddLink: () => void;
  onDeleteLink: (url: string) => void;
}

export const MediaAttachmentModal = ({
  isOpen,
  activeTab,
  documentsData,
  imagesData,
  currentUrls,
  linkInput,
  linkError,
  isUploading,
  isDeleting,
  maxItemsPerCategory,
  allowedDocumentExtensions,
  allowedImageExtensions,
  readOnly = false,
  onClose,
  onTabChange,
  onDocumentUpload,
  onImageUpload,
  onDownload,
  onDelete,
  onLinkInputChange,
  onAddLink,
  onDeleteLink,
}: MediaAttachmentModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 !mt-0" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-[calc(100%-2rem)] md:w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lampiran Media</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 overflow-x-auto no-scrollbar">
          <TabButton
            active={activeTab === 'documents'}
            icon={<FileText className="w-4 h-4" />}
            label="Dokumen"
            count={documentsData.length}
            onClick={() => onTabChange('documents')}
          />
          <TabButton
            active={activeTab === 'images'}
            icon={<ImageIcon className="w-4 h-4" />}
            label="Gambar"
            count={imagesData.length}
            onClick={() => onTabChange('images')}
          />
          <TabButton
            active={activeTab === 'links'}
            icon={<Link2 className="w-4 h-4" />}
            label="Tautan"
            count={currentUrls.length}
            onClick={() => onTabChange('links')}
          />
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-4">
              <MediaUploadArea
                id="document-upload"
                label="Unggah Dokumen (PDF, DOC, DOCX, XLS, XLSX, TXT - Maks. 10MB)"
                accept={allowedDocumentExtensions}
                isLoading={isUploading}
                isDisabled={readOnly || documentsData.length >= maxItemsPerCategory}
                maxReachedMessage={
                  documentsData.length >= maxItemsPerCategory
                    ? `Jumlah dokumen telah mencapai batas maksimum: ${maxItemsPerCategory}`
                    : undefined
                }
                loadingMessage="Mengunggah dokumen..."
                loadingColor="blue"
                onChange={onDocumentUpload}
              />

              {documentsData.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Belum ada dokumen yang diunggah</div>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {documentsData.map(doc => (
                    <MediaListItem
                      key={doc.id}
                      id={doc.id}
                      name={doc.name}
                      size={doc.size}
                      createdAt={doc.created_at}
                      type="document"
                      onDownload={onDownload}
                      onDelete={onDelete}
                      isDeleting={isDeleting}
                      canDelete={!readOnly}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-4">
              <MediaUploadArea
                id="image-upload"
                label="Unggah Gambar/Video (JPG, JPEG, PNG, MP4, MOV)"
                accept={allowedImageExtensions}
                isLoading={isUploading}
                isDisabled={readOnly || imagesData.length >= maxItemsPerCategory}
                maxReachedMessage={
                  imagesData.length >= maxItemsPerCategory
                    ? `Jumlah gambar/video telah mencapai batas maksimum: ${maxItemsPerCategory}`
                    : undefined
                }
                loadingMessage="Mengunggah gambar..."
                loadingColor="green"
                onChange={onImageUpload}
              />

              {imagesData.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Belum ada gambar yang diunggah</div>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {imagesData.map(img => (
                    <MediaListItem
                      key={img.id}
                      id={img.id}
                      name={img.name}
                      size={img.size}
                      createdAt={img.created_at}
                      type="image"
                      onDownload={onDownload}
                      onDelete={onDelete}
                      isDeleting={isDeleting}
                      canDelete={!readOnly}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Links Tab */}
          {activeTab === 'links' && (
            <LinksList
              links={currentUrls}
              linkInput={linkInput}
              linkError={linkError}
              maxReached={currentUrls.length >= maxItemsPerCategory}
              maxItems={maxItemsPerCategory}
              onLinkInputChange={onLinkInputChange}
              onAddLink={onAddLink}
              onDeleteLink={onDeleteLink}
              readOnly={readOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface TabButtonProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  count: number;
  onClick: () => void;
}

const TabButton = ({ active, icon, label, count, onClick }: TabButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
    }`}
  >
    <div className="flex items-center gap-2">
      {icon}
      {label}
      {count > 0 && (
        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold text-white bg-blue-600 rounded-full">
          {count}
        </span>
      )}
    </div>
  </button>
);
